// Price Polling Service
// Replaces WebSocket real-time price feeds with REST API polling for stability
// Polls MEXC REST API every 5 minutes for all position tickers

import { getMexcApiClient } from './mexcApi';
import { TrailingStopLossEngine } from './trailingStopLossEngine';
import { tickerLogger } from '../utils/logger';
import type { Ticker } from '../types/mexc';

// Polling interval: 1 minute (60,000 ms)
const POLL_INTERVAL_MS = 1 * 60 * 1000;

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 5000;

export type PricePollingEventType = 'poll_start' | 'poll_complete' | 'poll_error' | 'price_update';

export interface PricePollingEvent {
    type: PricePollingEventType;
    timestamp: number;
    data?: {
        tickerCount?: number;
        error?: string;
        ticker?: Ticker;
    };
}

export class PricePollingService {
    private static instance: PricePollingService;
    private isRunning = false;
    private pollingInterval: NodeJS.Timeout | null = null;
    private tickerCache: Record<string, Ticker> = {};
    private lastPollTime = 0;
    private nextPollTime = 0;
    private eventListeners: Array<(event: PricePollingEvent) => void> = [];
    private pollCount = 0;

    private constructor() { }

    static getInstance(): PricePollingService {
        if (!PricePollingService.instance) {
            PricePollingService.instance = new PricePollingService();
        }
        return PricePollingService.instance;
    }

    /**
     * Start the price polling service
     */
    start(): void {
        if (this.isRunning) {
            console.log('📊 Price polling service already running');
            return;
        }

        this.isRunning = true;
        console.log(`🚀 Starting Price Polling Service (${POLL_INTERVAL_MS / 1000}s intervals)`);

        // Execute first poll immediately
        this.pollPrices();

        // Schedule recurring polls
        this.pollingInterval = setInterval(() => {
            this.pollPrices();
        }, POLL_INTERVAL_MS);
    }

    /**
     * Stop the price polling service
     */
    stop(): void {
        if (!this.isRunning) return;

        this.isRunning = false;
        console.log('🛑 Stopping Price Polling Service');

        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }

        // Clear cache to free memory
        this.tickerCache = {};
    }

    /**
     * Poll prices from MEXC REST API
     */
    private async pollPrices(): Promise<void> {
        const startTime = Date.now();
        this.pollCount++;

        this.emit({
            type: 'poll_start',
            timestamp: startTime,
        });

        console.log(`📊 [Poll #${this.pollCount}] Starting price poll...`);

        let retries = 0;
        let success = false;

        while (retries < MAX_RETRIES && !success) {
            try {
                const mexcApi = getMexcApiClient();

                // Fetch all tickers at once (more efficient than individual calls)
                const tickers = await mexcApi.getTicker() as Ticker[];

                if (!Array.isArray(tickers)) {
                    throw new Error('Invalid ticker response: expected array');
                }

                // Update cache and notify TSL engine
                let updatedCount = 0;
                const tslEngine = TrailingStopLossEngine.getInstance();

                for (const ticker of tickers) {
                    if (ticker && ticker.symbol && typeof ticker.lastPrice === 'number' && ticker.lastPrice > 0) {
                        this.tickerCache[ticker.symbol] = ticker;

                        // Feed price to TSL engine
                        tslEngine.updatePrice(ticker.symbol, ticker.lastPrice);

                        // Log priority symbols (TSL-enabled positions)
                        tickerLogger.tickerUpdate(ticker.symbol, ticker.lastPrice, 'api', 'low');

                        this.emit({
                            type: 'price_update',
                            timestamp: Date.now(),
                            data: { ticker },
                        });

                        updatedCount++;
                    }
                }

                this.lastPollTime = Date.now();
                this.nextPollTime = this.lastPollTime + POLL_INTERVAL_MS;
                success = true;

                console.log(`✅ [Poll #${this.pollCount}] Price poll completed: ${updatedCount} symbols updated (${Date.now() - startTime}ms)`);

                this.emit({
                    type: 'poll_complete',
                    timestamp: this.lastPollTime,
                    data: { tickerCount: updatedCount },
                });

            } catch (error) {
                retries++;
                const errorMsg = error instanceof Error ? error.message : String(error);
                console.warn(`⚠️ [Poll #${this.pollCount}] Price poll failed (attempt ${retries}/${MAX_RETRIES}): ${errorMsg}`);

                if (retries < MAX_RETRIES) {
                    await this.delay(RETRY_DELAY_MS);
                } else {
                    console.error(`❌ [Poll #${this.pollCount}] Price poll failed after ${MAX_RETRIES} retries`);

                    this.emit({
                        type: 'poll_error',
                        timestamp: Date.now(),
                        data: { error: errorMsg },
                    });
                }
            }
        }
    }

    /**
     * Force an immediate price poll (bypasses interval)
     */
    async forcePoll(): Promise<void> {
        console.log('🔄 Force poll requested');
        await this.pollPrices();
    }

    /**
     * Get cached ticker for a symbol
     */
    getTicker(symbol: string): Ticker | null {
        return this.tickerCache[symbol] || null;
    }

    /**
     * Get all cached tickers
     */
    getAllTickers(): Record<string, Ticker> {
        return { ...this.tickerCache };
    }

    /**
     * Get last poll time
     */
    getLastPollTime(): number {
        return this.lastPollTime;
    }

    /**
     * Get next scheduled poll time
     */
    getNextPollTime(): number {
        return this.nextPollTime;
    }

    /**
     * Get time until next poll (ms)
     */
    getTimeUntilNextPoll(): number {
        return Math.max(0, this.nextPollTime - Date.now());
    }

    /**
     * Check if service is running
     */
    isServiceRunning(): boolean {
        return this.isRunning;
    }

    /**
     * Get poll interval in milliseconds
     */
    getPollIntervalMs(): number {
        return POLL_INTERVAL_MS;
    }

    /**
     * Subscribe to polling events
     */
    on(callback: (event: PricePollingEvent) => void): void {
        this.eventListeners.push(callback);
    }

    /**
     * Unsubscribe from polling events
     */
    off(callback: (event: PricePollingEvent) => void): void {
        const index = this.eventListeners.indexOf(callback);
        if (index > -1) {
            this.eventListeners.splice(index, 1);
        }
    }

    /**
     * Emit event to all listeners
     */
    private emit(event: PricePollingEvent): void {
        for (const listener of this.eventListeners) {
            try {
                listener(event);
            } catch (error) {
                console.error('Error in price polling event listener:', error);
            }
        }
    }

    /**
     * Utility delay function
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// Export singleton getter
export function getPricePollingService(): PricePollingService {
    return PricePollingService.getInstance();
}
